import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { arSA } from "date-fns/locale/ar-SA";
import { enUS } from "date-fns/locale/en-US";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { gregorianToHijri, hijriToGregorian, parseHijriInput, formatHijri } from "@/lib/hijri";

/* ─── Types ─────────────────────────────────────────────────────────────────── */
interface DatePickerProps {
  /** Current value as ISO date string (YYYY-MM-DD) or empty */
  value?: string;
  /** Callback when date changes – receives ISO date string */
  onChange?: (date: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional className for the trigger button */
  className?: string;
  /** Locale: "ar" or "en" */
  locale?: "ar" | "en";
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** ID for form integration */
  id?: string;
  /** Name for form integration */
  name?: string;
  /** Opt-in: show the Umm al-Qura Hijri equivalent + a Hijri quick-entry field (default off) */
  showHijri?: boolean;
  /** Accessible label for the trigger button (use when there is no visible <Label>) */
  ariaLabel?: string;
  /** Optional data-field attribute on the trigger (for DOM/test targeting) */
  dataField?: string;
}

/* ─── Helpers ───────────────────────────────────────────────────────────────── */
const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS_AR = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const WEEKDAYS_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isToday(d: Date) {
  return isSameDay(d, new Date());
}

/* ─── Component ─────────────────────────────────────────────────────────────── */
const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      className,
      locale = "ar",
      minDate,
      maxDate,
      id,
      name,
      showHijri = false,
      ariaLabel,
      dataField,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);

    // Parse value to Date
    const selectedDate = React.useMemo(() => {
      if (!value) return undefined;
      const d = parse(value, "yyyy-MM-dd", new Date());
      return isValid(d) ? d : undefined;
    }, [value]);

    // Viewed month/year
    const [viewYear, setViewYear] = React.useState(() => selectedDate?.getFullYear() ?? new Date().getFullYear());
    const [viewMonth, setViewMonth] = React.useState(() => selectedDate?.getMonth() ?? new Date().getMonth());

    // Sync view when value changes
    React.useEffect(() => {
      if (selectedDate) {
        setViewYear(selectedDate.getFullYear());
        setViewMonth(selectedDate.getMonth());
      }
    }, [selectedDate]);

    // ── Hijri (Umm al-Qura) — opt-in via showHijri (default off) ──
    // Mirror the selected Gregorian value into the Hijri quick-entry field.
    const [hijriInput, setHijriInput] = React.useState("");
    React.useEffect(() => {
      if (!showHijri) return;
      const h = value ? gregorianToHijri(value) : null;
      setHijriInput(
        h ? `${h.year}/${String(h.month).padStart(2, "0")}/${String(h.day).padStart(2, "0")}` : "",
      );
    }, [showHijri, value]);

    const isAr = locale === "ar";
    const months = isAr ? MONTHS_AR : MONTHS_EN;
    const weekdays = isAr ? WEEKDAYS_AR : WEEKDAYS_EN;
    const dateLocale = isAr ? arSA : enUS;

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

    // Year dropdown range. When min/max dates are supplied (e.g. a date of
    // birth bounded to [120 years ago, today]) the dropdown honours them. When
    // absent it falls back to a wide window: 120 years back so historical dates
    // (an owner/tenant born well before the old currentYear-50 ≈ 1976 floor) are
    // reachable, and ~50 years forward for future-dated fields (contract end, ad
    // expiry). An explicit minDate/maxDate always wins.
    const currentYear = new Date().getFullYear();
    const minYear = minDate ? minDate.getFullYear() : currentYear - 120;
    const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 49;
    const years = Array.from(
      { length: Math.max(1, maxYear - minYear + 1) },
      (_, i) => minYear + i,
    );

    const handlePrevMonth = () => {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear((y) => y - 1);
      } else {
        setViewMonth((m) => m - 1);
      }
    };

    const handleNextMonth = () => {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear((y) => y + 1);
      } else {
        setViewMonth((m) => m + 1);
      }
    };

    const handleSelectDate = (day: number) => {
      const d = new Date(viewYear, viewMonth, day);
      const iso = format(d, "yyyy-MM-dd");
      onChange?.(iso);
      setOpen(false);
    };

    const withinBounds = (date: Date) => {
      // Compare date-only: normalize to midnight so a wall-clock time on `date`
      // (e.g. the Today button passing `new Date()`) isn't read as "after" a
      // midnight-normalized maxDate of the same day.
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return false;
      if (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) return false;
      return true;
    };

    // Type a Hijri date → convert to Gregorian and emit it (still ISO). Invalid,
    // incomplete, or out-of-bounds input is held without firing onChange.
    const handleHijriInputChange = (raw: string) => {
      setHijriInput(raw);
      const parts = parseHijriInput(raw);
      if (!parts) return;
      const iso = hijriToGregorian(parts);
      if (!iso) return;
      const d = parse(iso, "yyyy-MM-dd", new Date());
      if (!isValid(d) || !withinBounds(d)) return;
      onChange?.(iso);
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    };

    const isDateDisabled = (day: number) => {
      const d = new Date(viewYear, viewMonth, day);
      if (minDate && d < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())) return true;
      if (maxDate && d > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())) return true;
      return false;
    };

    // Build calendar grid
    const blanks = Array.from({ length: firstDay }, (_, i) => i);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const displayValue = selectedDate
      ? format(selectedDate, "dd MMMM yyyy", { locale: dateLocale })
      : null;
    // Hijri label shown next to the Gregorian value (null when off or out of range).
    const hijriDisplay = showHijri && value ? formatHijri(value, isAr ? "ar" : "en") : null;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            data-field={dataField}
            variant="outline"
            role="combobox"
            disabled={disabled}
            className={cn(
              "w-full justify-between text-start font-normal h-11 px-3",
              !selectedDate && "text-muted-foreground",
              className,
            )}
            onClick={() => setOpen(true)}
          >
            <span className="truncate">
              {displayValue || placeholder || (isAr ? "اختر التاريخ" : "Select date")}
              {hijriDisplay && (
                <span className="text-muted-foreground ms-1" dir="ltr"> ({hijriDisplay})</span>
              )}
            </span>
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
          <div className="p-4 space-y-3" dir={isAr ? "rtl" : "ltr"}>
            {/* ── Header: Month & Year selectors ── */}
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-accent"
                onClick={isAr ? handleNextMonth : handlePrevMonth}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-2">
                <Select
                  value={String(viewMonth)}
                  onValueChange={(v) => setViewMonth(Number(v))}
                >
                  <SelectTrigger className="h-8 w-[110px] text-sm font-medium border-0 bg-accent/50 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[240px]">
                    {months.map((m, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={String(viewYear)}
                  onValueChange={(v) => setViewYear(Number(v))}
                >
                  <SelectTrigger className="h-8 w-[80px] text-sm font-medium border-0 bg-accent/50 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[240px]">
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-accent"
                onClick={isAr ? handlePrevMonth : handleNextMonth}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>

            {/* ── Weekday headers ── */}
            <div className="grid grid-cols-7 gap-0">
              {weekdays.map((wd) => (
                <div
                  key={wd}
                  className="h-9 flex items-center justify-center text-xs font-medium text-muted-foreground"
                >
                  {wd}
                </div>
              ))}
            </div>

            {/* ── Day grid ── */}
            <div className="grid grid-cols-7 gap-0">
              {blanks.map((b) => (
                <div key={`blank-${b}`} className="h-9 w-9" />
              ))}
              {days.map((day) => {
                const d = new Date(viewYear, viewMonth, day);
                const isSelected = selectedDate && isSameDay(d, selectedDate);
                const isTodayDate = isToday(d);
                const isDisabled = isDateDisabled(day);

                return (
                  <button
                    key={day}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => handleSelectDate(day)}
                    className={cn(
                      "h-9 w-9 rounded-full flex items-center justify-center text-sm transition-all duration-150",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground font-semibold shadow-sm",
                      !isSelected && isTodayDate && "bg-accent text-accent-foreground font-semibold",
                      isDisabled && "text-muted-foreground/40 pointer-events-none",
                    )}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* ── Hijri (Umm al-Qura) quick entry — opt-in ── */}
            {showHijri && (
              <div className="border-t pt-2 space-y-1" dir="ltr">
                <label className="block text-xs text-muted-foreground px-1">
                  {isAr ? "التاريخ الهجري (سنة/شهر/يوم)" : "Hijri date (yyyy/mm/dd)"}
                </label>
                <Input
                  value={hijriInput}
                  onChange={(e) => handleHijriInputChange(e.target.value)}
                  placeholder="1446/10/25"
                  inputMode="numeric"
                  className="h-8 text-sm tabular-nums"
                />
              </div>
            )}

            {/* ── Footer: Today button ── */}
            <div className="flex justify-center pt-1 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-primary hover:text-primary"
                disabled={!withinBounds(new Date())}
                onClick={() => {
                  const today = new Date();
                  if (!withinBounds(today)) return;
                  setViewYear(today.getFullYear());
                  setViewMonth(today.getMonth());
                  handleSelectDate(today.getDate());
                }}
              >
                {isAr ? "اليوم" : "Today"}
              </Button>
              {selectedDate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => {
                    onChange?.("");
                    setOpen(false);
                  }}
                >
                  {isAr ? "مسح" : "Clear"}
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);

DatePicker.displayName = "DatePicker";

export { DatePicker };
export type { DatePickerProps };
